import React, { useMemo, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GridApi } from '@ag-grid-community/core';
import { EnhancedSearchInput, SearchSuggestion } from '../CustomSearchInput';

interface CustomQuickFilterProps {
  data: Record<string, unknown>[];
  gridApi: GridApi | null;
  onFieldFilterChange?: (filter: {
    field: string | null;
    value: string;
  }) => void;
}

const SEARCHABLE_COLUMNS = [
  'brand',
  'aeName',
  'amName',
  'category',
  'typeOfPurchase',
  'sfOpportunityId',
  'rodeoAdName',
  'modifiedBy',
  'commentsByPoc',
  'slotNames',
  'exactBillingValue',
  'creatives',
  'entryId',
] as const;

const CustomQuickFilter: React.FC<CustomQuickFilterProps> = ({
  data,
  gridApi,
  onFieldFilterChange,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  // Track selected field for external handling
  const selectedFieldRef = useRef<string | null>(null);
  const searchFilterRef = useRef<string>('');

  const columnLabels: Record<string, string> = useMemo(
    () => ({
      brand: t('table-header-brand'),
      aeName: t('table-header-ae-name'),
      amName: t('table-header-am-name'),
      category: t('table-header-category'),
      typeOfPurchase: t('table-header-type-of-purchase'),
      sfOpportunityId: t('table-header-sf-opportunity-id'),
      rodeoAdName: t('table-header-rodeo-ad-name'),
      modifiedBy: t('table-header-modified-by'),
      commentsByPoc: t('table-header-comments-by-poc'),
      slotNames: t('table-header-slot-names'),
      exactBillingValue: t('table-header-exact-billing-value'),
      creatives: t('table-header-creatives'),
      entryId: t('table-header-entry-id'),
    }),
    [t]
  );

  const searchSuggestions: SearchSuggestion[] = useMemo(() => {
    const suggestionMap = new Map<string, SearchSuggestion>();

    data.forEach((row) => {
      SEARCHABLE_COLUMNS.forEach((field) => {
        const value = row[field];
        if (value === null || value === undefined) return;

        const normalizedValue = String(value).trim();
        if (!normalizedValue) return;

        const uniqueKey = `${field}:${normalizedValue}`;

        if (!suggestionMap.has(uniqueKey)) {
          suggestionMap.set(uniqueKey, {
            value: normalizedValue,
            field,
            meta: columnLabels[field],
          });
        }
      });
    });

    return Array.from(suggestionMap.values()).sort((a, b) =>
      a.value.localeCompare(b.value)
    );
  }, [data, columnLabels]);

  const handleSearchChange = useCallback(
    (value: string, suggestion?: SearchSuggestion) => {
      setSearchValue(value);

      if (!value) {
        // Clear all filters when search is empty
        selectedFieldRef.current = null;
        searchFilterRef.current = '';
        onFieldFilterChange?.({ field: null, value: '' });
        gridApi?.setQuickFilter('');
        return;
      }

      if (suggestion && suggestion.field) {
        // Field-specific filtering when a suggestion is selected
        selectedFieldRef.current = suggestion.field;
        searchFilterRef.current = value;

        onFieldFilterChange?.({ field: suggestion.field, value });
        gridApi?.setQuickFilter('');
      } else {
        // Free-text search across all columns when typing
        selectedFieldRef.current = null;
        searchFilterRef.current = '';

        // Apply quick filter for global search
        gridApi?.setQuickFilter(value);
      }
    },
    [gridApi, onFieldFilterChange]
  );

  return (
    <EnhancedSearchInput
      id="booking-search"
      handleOnChangeCb={handleSearchChange}
      value={searchValue}
      placeholder={t('search-bookings-placeholder')}
      suggestions={searchSuggestions}
      minCharactersForSuggestions={1}
      debounceMs={300}
    />
  );
};

export default CustomQuickFilter;
